/* Copied mostly from CreateGoalPanel */
import React, { useReducer, useState, useEffect } from 'react';

import { CircularProgress } from '@material-ui/core';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { useQueryClient } from '@tanstack/react-query';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { EditorState, ContentState } from 'draft-js';
import Task1 from 'models/task1';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import TrackingDetails from 'screens/common/sidepanel/DetailSection/GoalTracking/TrackingDetails';
import { selectCreateKRPanelData } from 'selectors/createKRPanelSelector';
import { selectOrgData } from 'selectors/orgData';
import styled from 'styled-components';
import { Button, StyledDrawer } from 'styles/admin';
import {
  StyledDivider,
  StyledButton,
  StyledTooltip,
  NewStyledIconButton,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { OKR_TAG_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getResetEditorState } from 'constants/draftJSHelpers';
import { findAndReplaceString } from 'constants/helper';
import { StyledIcon } from 'constants/icons';
import { sendCreatedKREvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import theme from 'constants/theme';

import { addTaggedEmployeesUpdateMacrosDeleteMentions } from 'services/helpers/task';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { createOrUpdateTask } from 'services/task';

import { Typography11, Typography13 } from 'uiToolkit/Typography';

import CreateGoalForm from './CreateGoalForm';
import { updateGoal, resetUpdateGoal } from './newGoalActions';
import { newGoalReducer } from './newGoalReducer';

const macroRegex = /#{{([^}])*}}/gm;

const Drawer = styled(StyledDrawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      padding: 0;
    }
  }
`;

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
`;
const FooterWrapper = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CreateGoalFormWrapper = styled.div`
  flex-grow: 1;
  padding: 1.5rem;
  flex: 1 1 0%;
  overflow: auto;
`;

const CustomStyledButton = styled(StyledButton)`
  &.MuiButton-root.Mui-disabled {
    color: white;
  }
`;

const TrackingPanelWrapper = styled.div`
  padding-left: 1.5rem;
  padding-top: 1rem;
`;

const Header = ({ closeClickHandler, headerText }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const updatedErrorText = findAndReplaceString(headerText, keyResultsName, 'Key Result');

  const updatedErrorTextWithoutGoal = findAndReplaceString(updatedErrorText, keyResultsName, 'Goal');
  return (
    <HeaderWrapper>
      <VerticallyAlignedFlexContainer>
        <Typography11>{updatedErrorTextWithoutGoal}</Typography11>
      </VerticallyAlignedFlexContainer>
      <NewStyledIconButton
        onClick={closeClickHandler}
        circular
        padding="big"
        topGutter={1.5}
        bottomGutter={1.5}
        style={{ marginLeft: 'auto' }}
      >
        <StyledIcon color="textSecondary" size="header">
          <CloseRoundedIcon />
        </StyledIcon>
      </NewStyledIconButton>
    </HeaderWrapper>
  );
};

const Footer = ({ canSave, saveHandler, cancelHandler, errorText, isLoading }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const updatedErrorText = findAndReplaceString(
    errorText,
    modifyString(keyResultsName, StringModifier.SENTENCE_CASE),
    'goal'
  );
  const finalErrorText = findAndReplaceString(
    updatedErrorText,
    modifyString(objectivesName, StringModifier.SENTENCE_CASE),
    'objective'
  );
  return (
    <FooterWrapper>
      <VerticallyAlignedFlexContainer style={{ width: '100%' }}>
        <Typography13
          onClick={cancelHandler}
          style={{ width: '8rem', marginRight: 'auto' }}
          weight={600}
          disabled={false}
        >
          Cancel
        </Typography13>
        {isLoading ? <CircularProgress size={24} style={{ marginRight: '1rem' }} /> : null}

        <StyledTooltip title={finalErrorText ?? ''}>
          <span>
            <Button
              size="large"
              blueOutline
              rightGutter={3}
              style={{
                padding: '0.5rem',
                fontWeight: '600',
                marginRight: '1.5rem',
              }}
              onClick={canSave ? () => saveHandler({ isSaveAndAnother: true }) : () => {}}
              disabled={!canSave || isLoading}
            >
              Save & Add Another
            </Button>
          </span>
        </StyledTooltip>
        <StyledTooltip title={finalErrorText ?? ''}>
          <span>
            <Button
              size="large"
              blue
              style={{ width: '6.25rem', padding: '0.5rem', fontWeight: '600' }}
              onClick={canSave ? saveHandler : () => {}}
              disabled={!canSave || isLoading}
            >
              Save & Close
            </Button>
          </span>
        </StyledTooltip>
      </VerticallyAlignedFlexContainer>
    </FooterWrapper>
  );
};

const initialState = {
  task: new Task1({
    editorState: EditorState.createEmpty(),
    measure: 'PERCENTAGE',
    actual: 0,
    startingProgressValue: 0,
  }),

  canSave: false,
  errorText: '',
};

// https://stackoverflow.com/a/39029383/8487836
const createResetState = (editorState) => ({
  task: new Task1({
    editorState: getResetEditorState(editorState),
    measure: 'PERCENTAGE',
    actual: 0,
    startingProgressValue: 0,
    description: '',
  }),

  canSave: false,
  errorText: '',
});

const CreateKRPanel = () => {
  const queryClient = useQueryClient();

  const { defaultGoalDueTS, defaultGoalStartTS, defaultGoalVisibility } = useSelector(selectOrgData, shallowEqual);

  const reduxDispatch = useDispatch();

  const {
    isOpen,
    onClose,
    onCancel,
    onSave,
    selectedTimeBoundPeriod,
    baseKR,
    ownerEmployee,
    subObjective,
    panelHeaderText,
    actionDispatchSource,
  } = useSelector(selectCreateKRPanelData, shallowEqual);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [goalDueDateTS, setGoalDueDateTS] = useState(selectedTimeBoundPeriod?.endTS ?? defaultGoalDueTS);

  const [goalStartDateTS, setGoalStartDateTS] = useState(selectedTimeBoundPeriod?.startTS ?? defaultGoalStartTS);

  const init = ({ task, canSave, errorText = '' }) => {
    const newKR = {
      ...task,
      to: { ...ownerEmployee },
      toIds: [{ ...ownerEmployee, ownerType: Task1.EMPLOYEE_TYPES.owner }],
      type: 'GOAL',
      dueDate: new Date(goalDueDateTS),
      startDate: new Date(goalStartDateTS),
      coloredStartDate: {
        color: 'grey',
        timeStamp: goalStartDateTS,
        reason: null,
      },
      coloredDueDate: {
        color: 'grey',
        timeStamp: goalDueDateTS,
        reason: null,
      },
      subObjective,
    };
    newKR.key = Task1.generateUniqueKey();
    return { task: newKR, canSave, errorText };
  };

  const [{ task: goalDetails, canSave, errorText }, dispatch] = useReducer(newGoalReducer, initialState, init);

  const [showTrackingDetails, setShowTrackingDetails] = useState(false);
  const [trackingOption, setTrackingOption] = useState(goalDetails ? goalDetails.source : null);

  const [isLoading, setIsLoading] = useState(false);

  const updateTrackingDetails = (trackingDetails) => {
    dispatch(updateGoal(trackingDetails));
  };

  const saveHandler = ({ isSaveAndAnother = false }) => {
    let newTask = goalDetails;

    if (!newTask?.startingProgressValue) newTask.startingProgressValue = 0;

    newTask = addTaggedEmployeesUpdateMacrosDeleteMentions(newTask);

    newTask.taskType = OKR_TAG_TYPES.OKR_TAG;

    if (newTask?.subObjective?.postCardId) {
      newTask.parentTaskId = newTask?.subObjective?.postCardId;
      newTask.taskType = OKR_TAG_TYPES.SUB_OKR_TAG;
    }

    setIsLoading(true);
    return createOrUpdateTask(newTask)
      .then((createdTask) => {
        sendCreatedKREvent({
          eventProperties: {
            source: actionDispatchSource,
            goalId: createdTask.id,
          },
        });

        reduxDispatch(showSuccessSnackbar(`${keyResultsName} successfully added!`));

        if (typeof onSave === 'function') onSave(createdTask);

        queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
        if (!isSaveAndAnother) {
          onCancel();
        } else {
          dispatch(
            resetUpdateGoal({
              ...init({
                task: {
                  ...createResetState(goalDetails?.editorState).task,
                  subObjective: newTask?.subObjective,
                },
                canSave: false,
                errorText: '',
              }).task,
              subObjective: newTask?.subObjective,
            })
          );
        }
      })
      .catch(() => reduxDispatch(showErrorSnackbar('An error occurred while creating the goal')))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const toggleShowTrackingDetails = (show, selected) => {
    setShowTrackingDetails(show);
    setTrackingOption(selected || null);
  };

  useEffect(() => {
    if (goalDetails?.subObjective?.timeBoundDetails && !goalDetails?.isDateEditedByUser) {
      const endTS = goalDetails?.subObjective?.timeBoundDetails?.endTS;
      const startTS = goalDetails?.subObjective?.timeBoundDetails?.startTS;
      setGoalDueDateTS(endTS);
      setGoalStartDateTS(startTS);
      updateTrackingDetails({
        ...goalDetails,
        dueDate: new Date(endTS),
        coloredDueDate: {
          ...(goalDetails.coloredDueDate ?? {}),
          color: endTS ? theme.palette.primary.main : theme.palette.grey.dark,
          timeStamp: endTS || null,
        },
        startDate: new Date(startTS),
        coloredStartDate: {
          ...(goalDetails.coloredStartDate ?? {}),
          color: startTS ? theme.palette.primary.main : theme.palette.grey.dark,
          timeStamp: startTS || null,
        },
      });
    }
  }, [goalDetails.subObjective]);

  useEffect(() => {
    if (!baseKR?.id) return;
    const {
      teams,
      teamId,
      target,
      subObjective,
      startingProgressValue,
      measure,
      startDate,
      dueDate,
      description,
      text = '',
    } = baseKR;

    const coloredStartDate = {
      color: 'grey',
      timeStamp: new Date(startDate),
      reason: null,
    };

    const coloredDueDate = {
      color: 'grey',
      timeStamp: new Date(dueDate),
      reason: null,
    };
    dispatch(
      updateGoal({
        teams,
        teamId,
        target,
        subObjective,
        startingProgressValue,
        measure,
        startDate,
        dueDate,
        description,
        coloredStartDate,
        coloredDueDate,
        editorState: EditorState.createWithContent(
          ContentState.createFromText(`Copy of - ${text.replaceAll(macroRegex, '')}`)
        ),
      })
    );
  }, [baseKR]);

  useEffect(() => {
    dispatch(
      updateGoal({
        subObjective,
      })
    );
  }, [subObjective]);

  useEffect(() => {
    // reset create goal panel data
    dispatch(
      resetUpdateGoal(
        init({
          task: { ...initialState.task, visibilityType: defaultGoalVisibility },
          canSave: false,
          errorText: '',
        }).task
      )
    );
  }, [isOpen]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => {
        onCancel();
      }}
      ModalProps={{
        onBackdropClick: () => {
          onCancel();
        },
      }}
    >
      {showTrackingDetails ? (
        <TrackingPanelWrapper>
          <VerticallyAlignedFlexContainer>
            <NewStyledIconButton padding="small" onClick={() => toggleShowTrackingDetails(false)} rightGutter={1}>
              <StyledIcon size="header" color="textSecondary">
                <ArrowBackRoundedIcon />
              </StyledIcon>
            </NewStyledIconButton>
          </VerticallyAlignedFlexContainer>
          <StyledDivider variant="fullWidth" topGutter={2} bottomGutter={2} />
          <TrackingDetails
            toggleShowTrackingDetails={toggleShowTrackingDetails}
            trackingOption={trackingOption}
            goalDetails={goalDetails}
            // getTaskData={getData}
          />
        </TrackingPanelWrapper>
      ) : (
        <Wrapper>
          <Header
            closeClickHandler={() => {
              onClose();
            }}
            headerText={actionDispatchSource === 'Home' ? `Add ${keyResultsName}` : panelHeaderText}
          />
          <StyledDivider variant="fullWidth" />

          <CreateGoalFormWrapper>
            <CreateGoalForm
              goalDetails={goalDetails}
              updateTask={updateTrackingDetails}
              toggleShowTrackingDetails={toggleShowTrackingDetails}
              dispatch={dispatch}
              parentPageSelectedTimeBoundPeriod={selectedTimeBoundPeriod}
              goalDueDateTS={goalDueDateTS}
              goalStartDateTS={goalStartDateTS}
              setGoalDueDateTS={setGoalDueDateTS}
              setGoalStartDateTS={setGoalStartDateTS}
              defaultGoalVisibility={defaultGoalVisibility}
            />
          </CreateGoalFormWrapper>
          <StyledDivider variant="fullWidth" />

          <Footer
            isLoading={isLoading}
            canSave={canSave}
            saveHandler={saveHandler}
            cancelHandler={() => {
              onCancel();
            }}
            errorText={errorText}
          />
        </Wrapper>
      )}
    </Drawer>
  );
};

export default CreateKRPanel;
